import React, { useState } from "react";
import { toast } from "sonner";
import api, { formatError } from "../lib/api";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Link, useParams } from "react-router-dom";
import { Lock, Newspaper, Share2, ArrowLeft, FileText } from "lucide-react";
import { useApi } from "../hooks/useApi";
import { useAuth } from "../context/AuthContext";
import { RichView } from "./RichEditor";
import { PageHero, Section } from "./Page";
import { monthLabel } from "../lib/dates";

const src = (u) => (u && u.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

export function IssueCard({ n, base }) {
  return (
    <Link to={`${base}/${n.slug}`} className="group glass flex flex-col overflow-hidden rounded-2xl transition-transform hover:-translate-y-0.5" data-testid={`newsletter-card-${n.id}`}>
      {n.cover_url ? <img src={src(n.cover_url)} alt="" className="h-40 w-full object-cover" loading="lazy" /> : <div className="flex h-40 items-center justify-center bg-crimson/90 text-gold"><Newspaper size={40} /></div>}
      <div className="p-4">
        <p className="eyebrow text-gold">{monthLabel(n.month)}{n.visibility === "members" && <span className="ml-2 inline-flex items-center gap-1 text-crimson"><Lock size={10} /> Members</span>}</p>
        <h3 className="mt-1 font-serif-display text-lg font-bold text-crimson group-hover:underline">{n.title}</h3>
        {n.intro && <p className="mt-1 line-clamp-3 text-sm text-foreground/75">{n.intro}</p>}
      </div>
    </Link>
  );
}

export function SubscribeBox() {
  const [f, setF] = useState({ name: "", email: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const { data } = await api.post("/newsletters/subscribe", f); setDone(data); toast.success("Almost there — please check your inbox."); }
    catch (err) { toast.error(formatError(err.response?.data?.detail) || "Please check your email address"); }
    finally { setBusy(false); }
  };
  if (done) return <p className="rounded-2xl bg-palm/10 p-4 text-sm text-palm" data-testid="newsletter-subscribed">Medaase! If {f.email} isn't already on our list, we've just emailed it a confirmation link — click it to start receiving issues. Check your spam folder if it doesn't arrive in a few minutes.</p>;
  return (
    <form onSubmit={submit} className="glass flex flex-col gap-2 rounded-2xl p-4 sm:flex-row sm:items-end" data-testid="newsletter-subscribe">
      <div className="flex-1"><p className="font-serif-display text-lg font-bold text-crimson">Get the newsletter by email</p><p className="text-xs text-muted-foreground">Friends and family of the Association can subscribe to public issues. Unsubscribe any time.</p></div>
      <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Your name" className="sm:w-40" data-testid="subscribe-name" />
      <Input type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="you@example.com" className="sm:w-56" data-testid="subscribe-email" />
      <Button type="submit" disabled={busy} className="bg-crimson hover:bg-crimson-hover" data-testid="subscribe-submit">{busy ? "Adding…" : "Subscribe"}</Button>
    </form>
  );
}

export function NewsletterArchive({ base = "/newsletter", hero = true }) {
  const { data } = useApi("/newsletters");
  const { user } = useAuth();
  const body = (
    <>
      {data && data.length === 0 && <p className="text-sm text-muted-foreground" data-testid="newsletter-empty">No issues published yet.</p>}
      {!user && hero && <p className="mb-4 text-sm text-muted-foreground">Members: <Link to="/login" className="font-semibold text-palm underline">sign in</Link> to read members-only issues.</p>}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="newsletter-archive">{(data || []).map((n) => <IssueCard key={n.id} n={n} base={base} />)}</div>
    </>
  );
  if (!hero) return <div data-testid="portal-newsletter">{body}</div>;
  return (
    <div data-testid="newsletter-page">
      <PageHero eyebrow="Stay connected" title="Newsletter" subtitle="Monthly news from the Okuapemman Association of Massachusetts — events, members, celebrations and more." />
      <Section>{body}{!user && <div className="mt-8"><SubscribeBox /></div>}</Section>
    </div>
  );
}

export function IssueAttachment({ n, compact = false }) {
  const url = src(n.attachment_url);
  if (n.attachment_type === "image") return <img src={url} alt={n.title} className={`${compact ? "mt-2 max-h-72" : "mt-6 w-full"} rounded-2xl object-contain`} data-testid="newsletter-attachment-image" />;
  return (
    <div className="mt-6" data-testid="newsletter-attachment-pdf">
      <iframe title={n.attachment_name || "Newsletter PDF"} src={`${url}#view=FitH`} className={`w-full rounded-2xl border border-gold/30 bg-white ${compact ? "h-72" : "h-[75vh]"}`} />
      <a href={url} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 rounded-full bg-crimson px-4 py-2 text-sm font-semibold text-parchment hover:bg-crimson-hover" data-testid="newsletter-attachment-open"><FileText size={14} /> Open / download {n.attachment_name || "PDF"}</a>
    </div>
  );
}

export function NewsletterIssue({ base = "/newsletter", hero = true }) {
  const { slug } = useParams();
  const { data, error } = useApi(`/newsletters/${slug}`);
  const share = () => {
    const url = window.location.href;
    if (navigator.share) navigator.share({ title: data.title, url }).catch(() => {});
    else window.open(`https://wa.me/?text=${encodeURIComponent(`${data.title} — ${url}`)}`, "_blank", "noreferrer");
  };
  if (error) return <Section><p className="text-sm text-muted-foreground" data-testid="newsletter-issue-error">{error.response?.data?.detail || "Issue not found."} <Link to="/login" className="font-semibold text-palm underline">Sign in</Link></p></Section>;
  if (!data) return null;
  const content = (
    <article className="mx-auto max-w-3xl" data-testid={`newsletter-issue-${data.id}`}>
      <Link to={base} className="inline-flex items-center gap-1 text-sm text-palm hover:underline" data-testid="newsletter-back"><ArrowLeft size={14} /> All issues</Link>
      {data.cover_url && <img src={src(data.cover_url)} alt="" className="mt-4 w-full rounded-2xl object-cover" />}
      {data.intro && <p className="mt-6 text-lg leading-relaxed text-foreground/85">{data.intro}</p>}
      {data.attachment_url && <IssueAttachment n={data} />}
      {data.sections.map((s, i) => (
        <section key={s.heading + i} className="mt-8" data-testid={`newsletter-section-${i}`}>
          <h2 className="font-serif-display text-2xl font-bold text-crimson border-b-2 border-gold/60 pb-1">{s.heading}</h2>
          {s.image_url && <img src={src(s.image_url)} alt="" className="mt-3 w-full rounded-xl object-cover" loading="lazy" />}
          <RichView html={s.body} className="mt-3" />
        </section>
      ))}
      {data.visibility === "public" && <button onClick={share} className="mt-8 inline-flex items-center gap-1 rounded-full bg-palm px-4 py-2 text-sm font-semibold text-parchment hover:bg-palm/90" data-testid="newsletter-share"><Share2 size={14} /> Share this issue</button>}
    </article>
  );
  if (!hero) return <div className="glass rounded-2xl p-6">{content}</div>;
  return (
    <div>
      <PageHero eyebrow={monthLabel(data.month)} title={data.title} subtitle={data.visibility === "members" ? "Members-only issue" : "Okuapemman Association of Massachusetts"} />
      <Section>{content}</Section>
    </div>
  );
}
